import {
  IImageManagementTaskDTO,
  ImageManagementActionDTO,
  ImageManagementResultDTO,
  ImageManagementTaskDTO,
  ImagerGroupsDTO,
  ImagerQuotaDTO,
  SearchConstraintRootDTO,
} from 'business/models/Models';
import ImageManagementTaskService from 'business/services/ImageManagementTaskService';
import RMCustomButton, {
  RMCustomButtonProps,
} from 'components/customComponents/CommonComponents/RMCustomButton/RMCustomButton';
import { CustomCheckBox, SelectInput } from 'components/input';
import EAlertType from 'enums/EAlertType';
import ESnackBarPosition from 'enums/ESnackBarPosition';
import React, { useEffect, useState } from 'react';
import {
  useImageManagementTasks,
  useImageManagementTasksResults,
  useIsSelectedTaskPreview,
  useIsSelectedTaskRunning,
  useSelectedImageTaskId,
} from 'redux/hooks/imageManagementTask';
import { showAlertSnackBar } from 'utils/Global';
import { actionButtonsEnable, iconSize } from '../../CrystalContainer/Constants';
import { TableColumns, ActionRequestStatus } from '../constants';
import { tableFilter } from '../tableFilter';
import { ActionOperationType, buttonsIconSize, JobTypeDTO } from './constants';
import {
  useImageManagementClearResults,
  useOnStartImageTask,
  useUpdateIsError,
  useUpdateIsSelectedTaskRunning,
} from './hooks';
import ImageStoreInfoModal from './TaskOption/ImageStoreInfoModal';
import { nanoid } from '@reduxjs/toolkit';
import { useRMDispatch } from 'redux/hooks';
import { removeBackGroundJob } from 'redux/reducers/backgroundJobsReducer';
import { StatusField } from './Results';

export const TASK_NAME_MAXIMUM_LENGTH = 64;

export const getCurrentOperationType = (action) => {
  return ActionOperationType[action?.actionType];
};

export const taskButtons = (
  onAddTask,
  onDuplicateTask,
  onDeleteTask,
  isDisabled,
  isLoading,
  isTaskSelected,
): RMCustomButtonProps[] => {
  return [
    {
      iconProps: { id: 'imt-taskbuttons-addplus', name: 'AddPlus', fill: 'white' },
      onClick: onAddTask,
      titleTip: 'Add',
      contentContainerStyle: {
        width: '2.25rem',
        height: '2.25rem',
        backgroundColor: actionButtonsEnable.add,
      },
      isDisabled: isLoading,
    },
    {
      iconProps: { id: 'imt-taskbuttons-duplicate', name: 'Duplicate', fill: 'white' },
      onClick: onDuplicateTask,
      titleTip: 'Duplicate',
      contentContainerStyle: {
        width: '2.25rem',
        height: '2.25rem',
        backgroundColor: actionButtonsEnable.add,
      },
      isDisabled: !isTaskSelected,
    },
    {
      iconProps: {
        id: 'imt-taskbuttons-delete',
        name: 'Delete',
        fill: 'white',
        ...iconSize,
      },
      onClick: onDeleteTask,
      titleTip: 'Delete',
      contentContainerStyle: {
        width: '2.25rem',
        height: '2.25rem',
        backgroundColor: actionButtonsEnable.delete,
      },
      isDisabled: isDisabled,
    },
  ] as RMCustomButtonProps[];
};

export const storageHeaderColumns: {
  accessor: string;
  Header: string;
  className?: string;
}[] = [
  {
    accessor: 'name',
    Header: 'Name',
  },
  {
    accessor: 'directory',
    Header: 'Directory',
  },
  {
    accessor: 'space',
    Header: 'Space (GB)',
  },
];

export const showUnsavedMessage = (showModal, hideModal) => {
  showModal({
    body: 'There are unsaved changes. Please save or discard those.',
    heading: 'Warning',
    okButtonText: 'OK',
    isOkOnly: true,
    onOk: hideModal,
  });
};

export const showLargerTaskNameMessage = (
  showModal,
  hideModal,
  duplicatedName: string,
) => {
  const truncateCharacters: number = duplicatedName.length - 64;
  showModal({
    body: `The name for copy '${duplicatedName}' is too large. Please truncate the name of original file by ${truncateCharacters.toString()} characters in order to create its copy.`,
    heading: 'Warning',
    okButtonText: 'OK',
    isOkOnly: true,
    onOk: hideModal,
  });
};

export const getStorageInfoModalProps = (imageStoreList, onClickRefresh) => {
  const headerProps = {
    body: <ImageStoreInfoModal imageStoreList={imageStoreList} />,
    dialogClassName: 'crystal_modal',
    showButtons: true,
    headerClassName: 'flex flex-row mb-3 ',
    headerStyle: { backgroundColor: '#FCE0C1' },
    titleClr: 'black',
    okButtonText: 'Refresh',
    onOk: onClickRefresh,
    heading: 'Storage Information',
    isOkOnly: true,
  };

  return headerProps;
};

export const renderCheckbox = (
  value,
  onChange,
  disabled,
  label = '',
  mainContainerStyle = {},
  labelStyle = {},
) => {
  return (
    <CustomCheckBox
      id={`rendercheckbox-${label}`}
      onChange={onChange}
      label={label}
      checked={value}
      mainContainerStyle={mainContainerStyle}
      labelStyle={labelStyle}
      disabled={disabled}
    />
  );
};

const onClickStop = (
  selectedTask: IImageManagementTaskDTO,
  setIsSelectedTaskRunning: (field: boolean) => any,
  setIsError: (field: boolean) => any,
  clearJob: (field: number) => any,
) => {
  const dataService = new ImageManagementTaskService();
  dataService.stop(selectedTask.id).then(() => {
    setIsSelectedTaskRunning(false);
    setIsError(true);
    clearJob(selectedTask.id);
  });
};

export const resultTabButtons = (
  viewLogsOnClick: () => void,
  downloadLogsOnClick: () => void,
  setProgress: (val: number) => any,
  results: ImageManagementResultDTO[],
): RMCustomButtonProps[] => {
  const imageManagementTasks = useImageManagementTasks();
  const selectedImageManagementTaskId = useSelectedImageTaskId();
  const selectedTask = imageManagementTasks?.find(
    (task) => task.id === selectedImageManagementTaskId,
  );
  const imageManagementTaskResult = useImageManagementTasksResults();
  const onStartImageTask = useOnStartImageTask();
  const isTaskSelected = selectedImageManagementTaskId ?? false ? true : false;
  const isRunning: boolean = useIsSelectedTaskRunning() ?? false;
  const isPreview: boolean = useIsSelectedTaskPreview() ?? false;
  const setIsSelectedTaskRunning = useUpdateIsSelectedTaskRunning();
  const clearResults = useImageManagementClearResults();
  const setIsError = useUpdateIsError();
  const [taskStarted, setTaskStarted] = useState(false);
  const dispatch = useRMDispatch();

  useEffect(() => {
    setTaskStarted(false);
  }, [selectedImageManagementTaskId]);

  const clearJob = (taskId: number) => {
    dispatch(
      removeBackGroundJob({ id: selectedTask.id, type: JobTypeDTO.ImageManangement }),
    );
  };

  const onClickStart = async () => {
    const runTask = async (
      taskOverrides: { isPreview: boolean; disableProgress: boolean },
      successMessage?: string,
    ) => {
      const task = { ...selectedTask, ...taskOverrides };
      const result = await onStartImageTask(task, taskOverrides.disableProgress);

      const isSuccess = result.meta.requestStatus === ActionRequestStatus.fulfilled;
      setIsSelectedTaskRunning(isSuccess);
      setIsError(!isSuccess);
      setTaskStarted(isSuccess);

      if (isSuccess && !successMessage) {
        return isSuccess;
      }

      showAlertSnackBar(
        isSuccess ? EAlertType.SUCCESS : EAlertType.ERROR,
        ESnackBarPosition.TOP,
        isSuccess ? successMessage : 'Task failed.',
      );

      return isSuccess;
    };

    // Still in preview mode but user has cleared the result
    if (isPreview && results.length === 0) {
      // re-retrieve result again before starting actual action
      if (!(await runTask({ isPreview: true, disableProgress: true }, null))) {
        return;
      }
    }

    await runTask({ isPreview: false, disableProgress: false }, 'Task has been started.');
  };

  const handleClearResults = () => {
    clearResults();
  };

  return [
    {
      title: 'START',
      mainContainerClass: 'ml-[8px] h-[55%] w-[70px]',
      contentContainerClass: 'h-[100%] w-[100%] bg-[#4CAF50]',
      titleClass: 'text-[14px] text-[#FFFFFF]',
      onClick: onClickStart,
      isDisabled:
        !isTaskSelected ||
        isRunning ||
        selectedTask?.imageManagementActionList?.length === 0,
      buttonMainContainerStyle: { fontFamily: 'Roboto' },
    },
    {
      title: 'CANCEL',
      mainContainerClass: 'ml-[8px] h-[55%] w-[70px]',
      contentContainerClass:
        'h-[100%] w-[100%] ' + (isRunning === true ? 'bg-[red]' : 'bg-[#CACACA]'),
      titleClass: 'text-[14px] text-[#FFFFFF]',
      onClick: () =>
        onClickStop(selectedTask, setIsSelectedTaskRunning, setIsError, clearJob),
      isDisabled: !isRunning,
      buttonMainContainerStyle: { fontFamily: 'Roboto' },
    },
    {
      iconProps: { name: 'Download', fill: 'white', width: '24', height: '24' },
      onClick: downloadLogsOnClick,
      mainContainerClass: 'ml-[8px] h-[55%] w-[6%]',
      contentContainerClass: 'viewErrors-button h-[100%] w-[100%] bg-[#DEE2E6]',
      titleTip: 'Download',
      isDisabled: (!isTaskSelected && !isRunning) || (isPreview && !taskStarted),
      buttonMainContainerStyle: { fontFamily: 'Roboto' },
    },
    {
      title: 'VIEW LOGS',
      mainContainerClass: 'ml-[8px] h-[55%] w-[88px]',
      contentContainerClass: 'viewErrors-button h-[100%] w-[100%]',
      titleClass: 'text-[14px]',
      onClick: viewLogsOnClick,
      buttonMainContainerStyle: { fontFamily: 'Roboto' },
      isDisabled: (!isTaskSelected && !isRunning) || (isPreview && !taskStarted),
    },
    {
      title: 'CLEAR RESULT',
      mainContainerClass:
        'ml-[8px] clearResult-mainContainer h-[55%] w-[111px] items-center',
      contentContainerClass: 'h-[100%] w-[100%]',
      titleClass: 'clearResult-button-title text-[14px]',
      onClick: handleClearResults,
      buttonMainContainerStyle: { fontFamily: 'Roboto' },
      isDisabled: !isTaskSelected || imageManagementTaskResult?.length === 0,
    },
  ] as RMCustomButtonProps[];
};

export const actionResultColumns: TableColumns[] = [
  {
    accessor: 'rowNumber',
    Header: 'Row',
    className: 'shortColumn',
    Filter: tableFilter(false, 'Row'),
  },
  {
    accessor: 'action',
    Header: 'Action',
    className: 'longColumn noWrapWithOverflow',
    Filter: tableFilter(false, 'Action'),
  },
  {
    accessor: 'actionStatus',
    Header: 'Status',
    className: 'shortColumn noWrapWithOverflow',
    Filter: tableFilter(false, 'Status'),
    Cell: ({ row }: any) => (
      <StatusField id={row.original.imageId} status={row.original.actionStatus} />
    ),
  },
  {
    accessor: 'experimentUserName',
    Header: 'Owner',
    className: 'mediumColumn noWrapWithOverflow',
    Filter: tableFilter(false, 'Owner'),
  },
  {
    accessor: 'experimentName',
    Header: 'Experiment Name',
    className: 'mediumColumn noWrapWithOverflow',
    Filter: tableFilter(false, 'Experiment Name'),
  },
  {
    accessor: 'plateId',
    Header: 'Plate ID',
    className: 'shortColumn',
    Filter: tableFilter(false, 'Plate ID'),
  },
  {
    accessor: 'plateNumber',
    Header: 'Plate Number',
    className: 'shortColumn',
    Filter: tableFilter(false, 'Plate Number'),
  },
  {
    accessor: 'wellNumber',
    Header: 'Well',
    className: 'shortColumn',
    Filter: tableFilter(false, 'Well'),
  },
  {
    accessor: 'dropNumber',
    Header: 'Drop',
    className: 'shortColumn',
    Filter: tableFilter(false, 'Drop'),
  },
  {
    accessor: 'regionId',
    Header: 'Region ID',
    className: 'shortColumn',
    Filter: tableFilter(false, 'Region ID'),
  },
  {
    accessor: 'regionName',
    Header: 'Region Name',
    className: 'shortColumn',
    Filter: tableFilter(false, 'Region Name'),
  },
  {
    accessor: 'captureResultId',
    Header: 'Capture ID',
    className: 'shortColumn noWrapWithOverflow',
    Filter: tableFilter(false, 'Capture ID'),
  },
  {
    accessor: 'captureResultImageBatchId',
    Header: 'Batch ID',
    className: 'shortColumn',
    Filter: tableFilter(false, 'Batch ID'),
  },
  {
    accessor: 'captureResultDateImaged',
    Header: 'Date Imaged',
    className: 'mediumColumn noWrap',
    Filter: tableFilter(false, 'Date Imaged'),
  },
  {
    accessor: 'wellDropScoreName',
    Header: 'Score Name',
    className: 'mediumColumn noWrapWithOverflow',
    Filter: tableFilter(false, 'Score Name'),
  },
  {
    accessor: 'imageId',
    Header: 'Image ID',
    className: 'shortColumn',
    Filter: tableFilter(false, 'Image ID'),
  },
  {
    accessor: 'imageTypeLongName',
    Header: 'Image Type',
    className: 'mediumColumn noWrapWithOverflow',
    Filter: tableFilter(false, 'Image Type'),
  },
  {
    accessor: 'imageTypeIndex',
    Header: 'Image Index',
    className: 'shortColumn',
    Filter: tableFilter(false, 'Image Index'),
  },
  {
    accessor: 'imagePixelSize',
    Header: 'Pixel Size',
    className: 'shortColumn',
    Filter: tableFilter(false, 'Pixel Size'),
  },
  {
    accessor: 'imageFileSize',
    Header: 'File Size',
    className: 'shortColumn',
    Filter: tableFilter(false, 'File Size'),
  },
  {
    accessor: 'imageStoreName',
    Header: 'Store Name',
    className: 'mediumColumn noWrapWithOverflow',
    Filter: tableFilter(false, 'Store Name'),
  },
  {
    accessor: 'imageStoreBasePath',
    Header: 'Store Base Path',
    className: 'longColumn noWrapWithOverflow',
    Filter: tableFilter(false, 'Store Base Path'),
  },
];

export const renderButtons = (iconName, isDisabled, onClick) => {
  return (
    <RMCustomButton
      mainContainerClass="h-full"
      iconProps={{ name: iconName, fill: 'white', ...buttonsIconSize }}
      contentContainerStyle={{
        width: '100%',
        height: '100%',
      }}
      onClick={onClick}
      isDisabled={isDisabled}
    />
  );
};

export const renderSelect = (
  options,
  value,
  onChange,
  containerStyle,
  isDisabled,
  className,
) => {
  return (
    <SelectInput
      className={className}
      options={options}
      style={containerStyle}
      value={value}
      onChange={onChange}
      isDisabled={isDisabled}
    />
  );
};

export const deserializeImageTaskDTO = (taskDto) => {
  return new ImageManagementTaskDTO({
    ...taskDto,
    imageManagementActionList:
      taskDto?.imageManagementActionList?.map((action) => {
        return new ImageManagementActionDTO({
          ...action,
          constraints: SearchConstraintRootDTO.fromJS(action.constraints),
        });
      }) ?? [],
  }) as ImageManagementTaskDTO;
};

export const deserializeImagerQuotaDTO = (quotaDTOList) => {
  return quotaDTOList.map((quota) => {
    return new ImagerQuotaDTO({
      ...quota,
      imagerGroups: quota.imagerGroups.map((group) => {
        return new ImagerGroupsDTO({
          ...group,
        });
      }),
    });
  }) as ImagerQuotaDTO[];
};

export function getUniqueTaskName(tasks, baseName) {
  let unique = false;
  let nameIndex = 1;
  const taskName = baseName;
  let uniqueName = '';

  while (!unique) {
    unique = true;
    uniqueName = taskName + ' (' + nameIndex + ')';

    for (let i = 0; i < tasks.length; i++) {
      if (uniqueName == tasks[i].title) {
        unique = false;
        nameIndex++;
        break;
      }
    }
  }

  return uniqueName;
}

export const injectIds = (group) => {
  // Skip if group already has an id
  if (group.id) {
    return group;
  }

  // Add ids to child rules and ruleGroups
  return {
    ...group,
    id: nanoid(),
    rules: group.rules.map((r) => ('rules' in r ? injectIds(r) : { ...r, id: nanoid() })),
  };
};
